import {Alert, Input, Select, Skeleton, Space} from "antd";
import {Controller, useFormContext} from "react-hook-form";
import {Link} from "react-router-dom";
import {TelegramIcon} from "../../../../pages/dashboard/automations/icons";
import {
  useGetIntegrationsStatus,
  useGetTelegramGroups,
} from "../../../../react-query-hooks/integrations/integrations";
import {CustomUrlLocationIcon} from "../../location-select";
import type {SubscriptionTiersFormValues} from "./types";

const optionContentStyle = {
  alignItems: "center",
  display: "inline-flex",
  gap: 8,
};

export function TierRedirectAfterPurchase({tierIndex}: {tierIndex: number}) {
  const {control, watch, setValue, clearErrors} =
    useFormContext<SubscriptionTiersFormValues>();
  const destination = watch(`tiers.${tierIndex}.redirectAfterPurchase`);
  const integrationsStatusQuery = useGetIntegrationsStatus({
    enabled: destination === "telegram",
  });
  const isTelegramConnected = integrationsStatusQuery.data?.telegramConnected === true;
  const telegramGroupsQuery = useGetTelegramGroups({
    enabled: destination === "telegram" && isTelegramConnected,
  });

  return (
    <Space direction="vertical" size={16} style={{display: "flex", width: "100%"}}>
      <Controller
        control={control}
        name={`tiers.${tierIndex}.redirectAfterPurchase`}
        render={({field}) => (
          <div>
            <label style={{display: "block", marginBottom: 8, fontWeight: 500}}>
              Where should we send customers after purchase?
            </label>
            <Select
              {...field}
              size="large"
              placeholder="Please select"
              style={{width: "100%"}}
              optionLabelProp="label"
              onChange={(nextValue) => {
                field.onChange(nextValue);
                if (nextValue !== "custom-url") {
                  setValue(`tiers.${tierIndex}.urlRedirect`, undefined, {
                    shouldDirty: true,
                  });
                  clearErrors(`tiers.${tierIndex}.urlRedirect`);
                }
                if (nextValue !== "telegram") {
                  setValue(`tiers.${tierIndex}.telegramGroupId`, undefined, {
                    shouldDirty: true,
                  });
                }
              }}
            >
              <Select.Option value="none" label="None">
                None
              </Select.Option>
              <Select.Option
                value="telegram"
                label={
                  <span style={optionContentStyle}>
                    <TelegramIcon size={20} />
                    <span>Telegram</span>
                  </span>
                }
              >
                <span style={optionContentStyle}>
                  <TelegramIcon size={20} />
                  <span>Telegram</span>
                </span>
              </Select.Option>
              <Select.Option
                value="custom-url"
                label={
                  <span style={optionContentStyle}>
                    <CustomUrlLocationIcon />
                    <span>Custom URL</span>
                  </span>
                }
              >
                <span style={optionContentStyle}>
                  <CustomUrlLocationIcon />
                  <span>Custom URL</span>
                </span>
              </Select.Option>
            </Select>
          </div>
        )}
      />

      {destination === "telegram" &&
        integrationsStatusQuery.data?.telegramConnected === false && (
          <Alert
            type="error"
            showIcon
            message={
              <span>
                You haven&apos;t connected your Telegram account yet.{" "}
                <Link
                  to="/automations/integrations/telegram"
                  style={{
                    color: "inherit",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  Connect Telegram
                </Link>
              </span>
            }
          />
        )}

      {destination === "telegram" && isTelegramConnected && (
        <Controller
          control={control}
          name={`tiers.${tierIndex}.telegramGroupId`}
          render={({field, fieldState: {error}}) => (
            <div>
              <label style={{display: "block", marginBottom: 8, fontWeight: 500}}>
                Select telegram group
              </label>
              {telegramGroupsQuery.isLoading ? (
                <Skeleton.Input active block size="large" style={{height: 40}} />
              ) : (
                <Select
                  {...field}
                  size="large"
                  placeholder="Please select"
                  style={{width: "100%"}}
                  options={[
                    {label: "None", value: "none"},
                    ...(telegramGroupsQuery.data ?? []).map((group) => ({
                      label: group.chatTitle,
                      value: group.chatId,
                    })),
                  ]}
                />
              )}
              {error?.message ? (
                <div style={{fontSize: "1.4rem", color: "#ff4d4f"}} role="alert">
                  {error.message}
                </div>
              ) : null}
            </div>
          )}
        />
      )}

      {destination === "custom-url" && (
        <Controller
          control={control}
          name={`tiers.${tierIndex}.urlRedirect`}
          render={({field, fieldState: {error}}) => (
            <div>
              <Input
                {...field}
                value={field.value ?? ""}
                size="large"
                placeholder="Enter Url"
                status={error?.message ? "error" : ""}
              />
              {error?.message ? (
                <div style={{fontSize: "1.4rem", color: "#ff4d4f"}} role="alert">
                  {error.message}
                </div>
              ) : null}
            </div>
          )}
        />
      )}
    </Space>
  );
}
